import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useProperty } from "@/contexts/PropertyContext";
import { Buildings, CaretDown, Check, House } from "@phosphor-icons/react";

/**
 * Rentlio-style property switcher — a compact dropdown that lets the user
 * scope the entire app (Kalendar / Rezervacije / Cijene…) to a single
 * property or view "Sve smještaje" at once.
 *
 * Rendered inside `TopBar`. Auto-hides when the tenant has ≤1 property.
 */
export default function PropertySwitcher() {
  const { properties, activePropertyId, activeProperty, setActivePropertyId, loading } = useProperty();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (loading && !properties.length) return null;
  // Show the switcher even for tenants with a single property — user relies on
  // it as a scope selector across Kokpit/Dolasci/Odlasci even when only one
  // property exists (they may add more soon).
  if (!properties || properties.length === 0) return null;

  const label = activeProperty ? activeProperty.name : "Svi smještaji";
  const initial = activeProperty ? (activeProperty.name?.[0] || "?").toUpperCase() : null;

  return (
    <div className="relative" ref={ref} data-testid="property-switcher">
      <button
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setPos({ top: r.bottom + 6, left: r.left });
          setOpen((v) => !v);
        }}
        className="flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-left text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
        data-testid="property-switcher-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          {activeProperty ? (
            <span className="text-[11px] font-bold">{initial}</span>
          ) : (
            <Buildings size={14} weight="bold" />
          )}
        </span>
        <span className="hidden max-w-[180px] truncate sm:inline">{label}</span>
        <CaretDown size={12} weight="bold" className="opacity-60" />
      </button>

      {open && createPortal(
        <>
          {/* Portaled to <body> so sticky calendar columns can never paint over it */}
          <div
            ref={menuRef}
            className="rw-pop fixed inset-x-3 z-[100] max-h-[70vh] overflow-hidden rounded-lg border border-border bg-popover shadow-xl sm:inset-x-auto"
            style={window.innerWidth >= 640 ? { top: pos?.top, left: pos?.left, width: 288 } : { top: pos?.top }}
            role="listbox"
            data-testid="property-switcher-menu"
          >
          <div className="border-b border-border px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Aktivni smještaj
          </div>
          <button
            onClick={() => { setActivePropertyId(null); setOpen(false); }}
            className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted ${
              !activePropertyId ? "bg-muted/60 font-semibold" : ""
            }`}
            data-testid="property-switcher-item-all"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <House size={14} weight="bold" />
            </span>
            <div className="flex-1">
              <div className="text-foreground">Svi smještaji</div>
              <div className="text-[11px] text-muted-foreground">Zbirni pregled ({properties.length})</div>
            </div>
            {!activePropertyId && <Check size={14} weight="bold" className="text-primary" />}
          </button>

          <div className="max-h-72 overflow-y-auto border-t border-border py-1">
            {properties.map((p) => (
              <button
                key={p.id}
                onClick={() => { setActivePropertyId(p.id); setOpen(false); }}
                className={`flex w-full min-w-0 items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                  activePropertyId === p.id ? "bg-muted/60 font-semibold" : ""
                }`}
                data-testid={`property-switcher-item-${p.id}`}
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-[11px] font-bold text-primary">
                  {(p.name?.[0] || "?").toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-foreground">{p.name}</div>
                  {p.city && <div className="truncate text-[11px] text-muted-foreground">{p.city}</div>}
                </div>
                {activePropertyId === p.id && <Check size={14} weight="bold" className="shrink-0 text-primary" />}
              </button>
            ))}
          </div>
        </div>
        </>,
        document.body,
      )}
    </div>
  );
}
